import { logSwallowed } from '@/lib/errors';
// ============================================================================
// chunkedUpload.ts — ЕДИНЫЙ механизм передачи файлов (SSoT/SRP, offline-first):
//   ЗАГРУЗКА: файл режется на чанки (4 МБ), грузятся по одному (RPC
//   StartChunkedUpload/UploadChunk) с прогрессом, паузой/возобновлением/отменой.
//   Чанки идемпотентны (докачка с места паузы; переживает перезагрузку).
//   ОФЛАЙН: файл целиком (Blob) кладётся в durable IndexedDB-очередь
//   (upload_queue:{baseId}) — пользователь видит «ожидает сети»; при
//   восстановлении — автоматическая чанковая загрузка.
//   ОДНОВРЕМЕННО: несколько передач — единый менеджер (пул параллельности,
//   события прогресса по каждой).
//   СКАЧИВАНИЕ больших файлов — через fileSync (hash-дельта, чанки Range).
// Везде применяется: FileListTab, вложения чата, реквизит «Файл».
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { idbGet, idbSet } from '@/lib/storage/idb';
import { createLogger } from '@/lib/logger';
import { isNotFoundError } from '@/lib/errors';
import { isEffectivelyOffline } from '@/lib/offlineFlag';

const log = createLogger('ChunkedUpload');

export const CHUNK_SIZE = 4 * 1024 * 1024; // 4 МБ
const CONCURRENCY = 3; // одновременных чанков на передачу
const PARALLEL_UPLOADS = 2; // одновременных ФАЙЛОВ

export type TransferStatus = 'pending' | 'uploading' | 'paused' | 'queued' | 'done' | 'error' | 'aborted';

export interface UploadJob {
  id: string;             // upload_id (глобально уникален, сервер)
  fileName: string;
  fileType: string;
  size: number;
  blob: Blob;
  status: TransferStatus;
  progress: number;       // 0..1
  uploadedBytes: number;
  error?: string;
  ownerId?: string;
  storageCode?: string;
  fileId?: string;        // id записи «Файлы» после сборки
  geoLat?: number;
  geoLon?: number;
  onComplete?: (job: UploadJob, fileId: string) => void;
  /** Прогресс передачи 0..1 (для отложенных разборов ЭКГ и других очередей). */
  onProgress?: (fraction: number) => void;
  // внутренние
  chunksTotal: number;
  chunksDone: number;
  aborted?: boolean;   // ОТМЕНА (чистит серверную передачу)
  paused?: boolean;    // ПАУЗА (сохраняет передачу для докачки)
}

// ============================================================================
// Очередь передач (SSoT — процесс; UI подписывается через listener)
// ============================================================================

type Listener = () => void;
const listeners = new Set<Listener>();
export function onUploadsChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emit() {
  for (const fn of listeners) fn();
}

/** Прогресс конкретной передачи (не роняет загрузку при сбое наблюдателя). */
function emitProgress(job: UploadJob) {
  if (!job.onProgress) return;
  try {
    job.onProgress(job.progress);
  } catch (e) {
    log.warn('upload progress listener failed', e);
  }
}

const jobs = new Map<string, UploadJob>();
let active = 0;

export function getUploadJobs(): UploadJob[] {
  return Array.from(jobs.values());
}

export function getUploadJob(id: string): UploadJob | undefined {
  return jobs.get(id);
}

// ============================================================================
// Offline-очередь (IndexedDB, durable): Blob переживает перезагрузку
// ============================================================================

interface QueuedUpload {
  id: string;
  fileName: string;
  fileType: string;
  size: number;
  blobType: string;
  blobArrayBuffer: ArrayBuffer; // Blob → ArrayBuffer для структурированного хранения
  ownerId?: string;
  storageCode?: string;
  geoLat?: number;
  geoLon?: number;
  ts: number;
}

function queueKey(baseId?: string): string {
  return `upload_queue:${baseId || 'default'}`;
}

async function getQueue(baseId?: string): Promise<QueuedUpload[]> {
  try {
    return (await idbGet<QueuedUpload[]>(queueKey(baseId))) || [];
  } catch {
    return [];
  }
}

async function setQueue(baseId: string | undefined, q: QueuedUpload[]) {
  try {
    await idbSet(queueKey(baseId), q);
  } catch (e) {
    log.warn('upload queue save failed', e);
  }
}

// ============================================================================
// ОТПРАВКА одного чанка (сеть) — идемпотентно
// ============================================================================

async function sendChunk(job: UploadJob, index: number, data: Uint8Array, isLast: boolean) {
  const r = await getSDK().files.uploadChunk({
    uploadId: job.id,
    index,
    data,
    isLast,
  } as any);
  // Последний чанк возвращает file_id собранного файла
  if (isLast && r?.fileId) job.fileId = String(r.fileId);
  return r;
}

async function startOnServer(job: UploadJob): Promise<{ uploadId: string; totalChunks: number }> {
  const totalChunks = Math.max(1, Math.ceil(job.size / CHUNK_SIZE));
  const r: any = await getSDK().files.startChunkedUpload({
    fileName: job.fileName,
    fileType: job.fileType,
    chunkSize: CHUNK_SIZE,
    totalChunks,
    totalSize: job.size,
    storageCode: job.storageCode || '',
    ownerId: job.ownerId || '',
    geoLat: job.geoLat || 0,
    geoLon: job.geoLon || 0,
  } as any);
  return { uploadId: String(r?.uploadId || ''), totalChunks: Number(r?.totalChunks || totalChunks) };
}

// Получение уже принятых чанков (докачка с места паузы/перезагрузки).
// Передача НЕ найдена → БРОСАЕТ (вызывающий создаёт новую StartChunkedUpload).
// Это НЕ фоллбэк: несуществующая передача = старт заново, детерминированно.
async function receivedChunks(uploadId: string): Promise<{ received: Set<number>; total: number; fileId: string }> {
  const r: any = await getSDK().files.getChunkedUpload({ uploadId } as any);
  return {
    received: new Set<number>((r?.receivedChunks || []).map(Number)),
    total: Number(r?.totalChunks || 0),
    fileId: String(r?.fileId || ''),
  };
}

// ============================================================================
// Полная загрузка файла онлайн (чанки, прогресс, пауза/отмена)
// ============================================================================

async function uploadOnline(job: UploadJob) {
  job.status = 'uploading';
  emit();
  try {
    // Передача уже существует (resume/докачка)? GetChunkedUpload → NotFound
    // означает «передачи нет» — создаём новую (StartChunkedUpload).
    let uploadId = job.id;
    let totalChunks = job.chunksTotal;
    let received = new Set<number>();
    let fileId = '';
    try {
      const st = await receivedChunks(uploadId);
      received = st.received;
      if (st.total > 0) totalChunks = st.total;
      fileId = st.fileId;
    } catch (e: any) {
      // Передачи с этим id нет (404 not_found) → создаём новую (StartChunkedUpload).
      // ЛЮБАЯ другая ошибка (сеть/авторизация/сервер) — пробрасываем явно:
      // молчаливый catch (e) { logSwallowed(e, 'lib/chunkedUpload.ts:180'); } создавал дубль передачи и «проглатывал» сбои.
      // isNotFoundError — SSoT: connect Code.NotFound=5 (число) ИЛИ "[not_found]".
      if (!isNotFoundError(e)) throw e;
      // RACE CONDITION (консультант, 2026-08-30): сразу после StartChunkedUpload
      // сервер пишет метаданные через os.WriteFile — до того как файл реально
      // записан (fsync), параллельный GetChunkedUpload может вернуть not_found,
      // и клиент бесконечно пересоздавал передачу. Ждём 150мс и пробуем ещё раз:
      // если передача УЖЕ создана — используем её (не дублируем).
      await new Promise((r) => setTimeout(r, 150));
      try {
        const st2 = await receivedChunks(uploadId);
        received = st2.received;
        if (st2.total > 0) totalChunks = st2.total;
        fileId = st2.fileId;
      } catch (e2: any) {
        // Всё ещё not_found — создаём новую передачу (StartChunkedUpload)
        // и считываем её состояние (детерминированно).
        if (!isNotFoundError(e2)) throw e2;
        const st = await startOnServer(job);
        uploadId = st.uploadId;
        totalChunks = st.totalChunks;
        received = new Set<number>();
      }
    }
    job.id = uploadId;
    job.chunksTotal = totalChunks;
    job.chunksDone = received.size;
    job.progress = totalChunks > 0 ? received.size / totalChunks : 0;
    job.uploadedBytes = Math.round(job.progress * job.size);
    emit();
    emitProgress(job);

    // Порциями (пул параллельности): загружаем недостающие чанки
    const missing: number[] = [];
    for (let i = 0; i < totalChunks; i++) {
      if (!received.has(i)) missing.push(i);
    }
    let cursor = 0;
    let inFlight = 0;
    const pump = async () => {
      while (cursor < missing.length) {
        if (job.aborted || job.paused) break;
        const idx = missing[cursor++];
        inFlight++;
        const start = idx * CHUNK_SIZE;
        const end = Math.min(job.size, start + CHUNK_SIZE);
        const data = new Uint8Array(await job.blob.slice(start, end).arrayBuffer());
        const isLast = idx === totalChunks - 1;
        try {
          await sendChunk(job, idx, data, isLast);
          job.chunksDone++;
          job.progress = job.chunksTotal > 0 ? job.chunksDone / job.chunksTotal : 0;
          job.uploadedBytes = Math.round(job.progress * job.size);
          emit();
          emitProgress(job);
        } catch (e: any) {
          if (job.aborted) break;
          job.status = 'error';
          job.error = e?.message || 'Ошибка загрузки чанка';
          emit();
          break;
        } finally {
          inFlight--;
        }
      }
      if (inFlight === 0) {
        const s = String(job.status);
        if (job.aborted || s === 'paused') {
          if (job.aborted) {
            // отменена — прервать на сервере (best-effort)
            try { await getSDK().files.abortChunkedUpload({ uploadId: job.id } as any); } catch { /* */ }
            job.status = 'aborted';
          }
          emit();
        } else if (s === 'error') {
          // уже ошибка
        } else {
          job.status = 'done';
          job.progress = 1;
          job.uploadedBytes = job.size;
          emit();
          emitProgress(job);
        }
      }
    };
    // Запускаем параллельные «качалки» (CONCURRENCY потоков на один файл)
    const workers: Promise<void>[] = [];
    for (let w = 0; w < CONCURRENCY; w++) workers.push(pump());
    await Promise.all(workers);
    // Определяем итог по флагам (narrowing status в замыканиях нестабилен)
    const st = String(job.status);
    if (st === 'done') {
      const fileId = job.fileId || '';
      job.onComplete?.(job, fileId);
    } else if (st === 'paused' || job.aborted) {
      // приостановлена/отменена — статус уже выставлен
    }
  } catch (e: any) {
    if (job.aborted) { job.status = 'aborted'; }
    else { job.status = 'error'; job.error = e?.message || 'Ошибка загрузки'; }
    emit();
  }
}

// onComplete — колбэк с file_id после сборки (вызывающий узнаёт id).
export type UploadComplete = (job: UploadJob, fileId: string) => void;

// ============================================================================
// ПУБЛИЧНЫЙ API
// ============================================================================

export interface UploadOptions {
  ownerId?: string;
  storageCode?: string;
  geoLat?: number;
  geoLon?: number;
  onComplete?: UploadComplete;
  /** Прогресс передачи 0..1 (для отложенных разборов ЭКГ и других очередей). */
  onProgress?: (fraction: number) => void;
}

/** Загрузить файл (единый механизм). Офлайн → durable-очередь; онлайн → чанки. */
export async function uploadFile(file: File, opts: UploadOptions = {}): Promise<UploadJob> {
  const blob = file;
  const job: UploadJob & { onComplete?: UploadComplete } = {
    id: crypto.randomUUID(),
    fileName: file.name,
    fileType: file.type || 'application/octet-stream',
    size: file.size,
    blob,
    status: 'pending',
    progress: 0,
    uploadedBytes: 0,
    chunksTotal: Math.max(1, Math.ceil(file.size / CHUNK_SIZE)),
    chunksDone: 0,
    ownerId: opts.ownerId,
    storageCode: opts.storageCode,
    geoLat: opts.geoLat,
    geoLon: opts.geoLon,
    onComplete: opts.onComplete,
    onProgress: opts.onProgress,
  };
  jobs.set(job.id, job);
  emit();

  if (typeof navigator === 'undefined' || isEffectivelyOffline()) {
    // Офлайн/«Автономный режим»: в durable-очередь (Blob → ArrayBuffer)
    job.status = 'queued';
    emit();
    try {
      const q = await getQueue();
      q.push({
        id: job.id, fileName: job.fileName, fileType: job.fileType, size: job.size,
        blobType: job.blob.type, blobArrayBuffer: await job.blob.arrayBuffer(),
        ownerId: opts.ownerId, storageCode: opts.storageCode,
        geoLat: opts.geoLat, geoLon: opts.geoLon, ts: Date.now(),
      });
      await setQueue(undefined, q);
    } catch (e: any) {
      job.status = 'error';
      job.error = e?.message || 'Не удалось поставить в офлайн-очередь';
      emit();
    }
    return job;
  }

  // Онлайн: лимит одновременных файлов (PARALLEL_UPLOADS)
  if (active >= PARALLEL_UPLOADS) {
    job.status = 'pending';
    emit();
    await waitForSlot();
  }
  active++;
  try {
    await uploadOnline(job);
  } finally {
    active--;
  }
  return job;
}

/** Отмена загрузки (прерывает чанки; офлайн — убирает из очереди). */
export async function abortUpload(id: string) {
  const job = jobs.get(id);
  if (!job) return;
  job.aborted = true;
  if (job.status === 'queued') {
    const q = await getQueue();
    await setQueue(undefined, q.filter((x) => x.id !== id));
    job.status = 'aborted';
    emit();
  }
  // uploading — флаг aborted прервёт pump; статус выставится там
}

/** Пауза загрузки (временно; возобновление — resumeUpload; передача сохраняется). */
export function pauseUpload(id: string) {
  const job = jobs.get(id);
  if (!job) return;
  if (job.status === 'uploading') {
    job.paused = true;   // НЕ aborted (иначе серверная передача отменится!)
    job.status = 'paused';
    emit();
  }
}

/** Возобновление (докачка недостающих чанков; GetChunkedUpload → докачка). */
export async function resumeUpload(id: string) {
  const job = jobs.get(id);
  if (!job || job.status !== 'paused') return;
  job.paused = false;
  job.aborted = false;
  job.status = 'uploading';
  active++;
  try {
    await uploadOnline(job);
  } finally {
    active--;
  }
}

/** Очистить завершённые/ошибки из списка (UI). */
export function clearFinishedUploads() {
  for (const [id, job] of jobs) {
    if (job.status === 'done' || job.status === 'aborted') jobs.delete(id);
  }
  emit();
}

// Ожидание свободного слота (очередь на клиенте)
function waitForSlot(): Promise<void> {
  return new Promise((resolve) => {
    const check = () => {
      if (active < PARALLEL_UPLOADS) resolve();
      else setTimeout(check, 200);
    };
    check();
  });
}

// ============================================================================
// FLUSH ОФЛАЙН-ОЧЕРЕДИ (при восстановлении сети/старте) — единый механизм
// ============================================================================

export async function flushUploadQueue(): Promise<number> {
  if (typeof navigator === 'undefined' || isEffectivelyOffline()) return 0;
  const q = await getQueue();
  if (q.length === 0) return 0;
  let flushed = 0;
  const remaining: QueuedUpload[] = [];
  for (const item of q) {
    if (active >= PARALLEL_UPLOADS) {
      remaining.push(item);
      continue;
    }
    try {
      const blob = new Blob([item.blobArrayBuffer], { type: item.blobType || 'application/octet-stream' });
      const job: UploadJob & { onComplete?: UploadComplete } = {
        id: item.id, fileName: item.fileName, fileType: item.fileType, size: item.size,
        blob, status: 'pending', progress: 0, uploadedBytes: 0,
        chunksTotal: Math.max(1, Math.ceil(item.size / CHUNK_SIZE)), chunksDone: 0,
        ownerId: item.ownerId, storageCode: item.storageCode,
        geoLat: item.geoLat, geoLon: item.geoLon,
      };
      jobs.set(job.id, job);
      emit();
      active++;
      try {
        await uploadOnline(job);
      } finally {
        active--;
      }
      if (job.status === 'done' || job.status === 'error') {
        flushed++;
      } else {
        remaining.push(item); // не завершилась — оставляем в очереди
      }
    } catch {
      remaining.push(item);
    }
  }
  await setQueue(undefined, remaining);
  return flushed;
}
